import { BreadCrumbTrail } from '@common/components/layout/nav/BreadCrumbTrail';
import { Authorized } from '@common/components/trimming/Authorized';
import { NotificationList } from '@common/components/utility';
import { useApp } from '@common/hooks';
import { AuthOperationUINames } from '@common/models/Enums/AuthOperationStrings';
import { componentUtility } from '@hospital/utils';
import classNames from 'classnames';
import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { AdminNavBar } from './nav/AdminNavBar';
import { AdminNavList } from './nav/AdminNavList';
import { HospitalMainNavBar } from './nav/HospitalMainNavBar';

export interface PageProps {
    hideBreadcrumbs?: boolean;
    pageContentClass?: string;
    positionSticky?: boolean;
}

const Page = (props: React.PropsWithChildren<PageProps>) => {
    const { children } = props;
    const pageNav = componentUtility.findByType(children, PageNav);
    const contentComponent = componentUtility.findByType(children, Content);
    const sideBarComponent = componentUtility.findByType(children, SideBar);
    const pageTitleComponent = componentUtility.findByType(children, PageTitle);

    if (!contentComponent) {
        throw new Error('Expected Page.Content child on Page component, not supplied');
    }
    return (
        <div className="sticky-top page-wrapper">
            {
                pageNav && pageNav
            }
            {
                !pageNav &&
                <HospitalMainNavBar />
            }
            <NotificationList />
            <Container fluid className="page g-0">
                <Row className="g-0 row-eq-height page-row">
                    <Col md={sideBarComponent ? 10 : 12} className="main-column g-0 d-flex flex-column">
                        {
                            !props.hideBreadcrumbs &&
                            <BreadCrumbTrail positionSticky={props.positionSticky} />
                        }
                        <div className={classNames("page-content flex-fill d-flex flex-column", props.pageContentClass ? props.pageContentClass : "m-3")}>
                            {
                                pageTitleComponent ? pageTitleComponent : null
                            }
                            {contentComponent}
                        </div>
                    </Col>
                    {
                        sideBarComponent &&
                        <Col className="side-bar" md={2}>
                            {sideBarComponent}
                        </Col>
                    }
                </Row>
            </Container>
        </div>
    )
};

const AdminPage = (props: React.PropsWithChildren<PageProps>) => {
    const { configuration } = useApp();

    const { children } = props;
    const contentComponent = componentUtility.findByType(children, Content);
    const sideBarComponent = componentUtility.findByType(children, SideBar);
    const pageTitleComponent = componentUtility.findByType(children, PageTitle);
    if (!contentComponent) {
        throw new Error('Expected Page.Content child on Page component, not supplied');
    }
    return (
        <Authorized if={configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanAccessHospitalAdmin)} deniedMessage="You are not authorized to view this page." >
            <div className="sticky-top page-wrapper">
                <AdminNavBar />
                <NotificationList />
                <Container fluid className="page g-0">
                    <Row className="g-0 row-eq-height page-row">
                        <Col className="page-sideBar page-sideBar__left gx-0" md={2} >
                            <AdminNavList />
                        </Col>
                        <Col md={sideBarComponent ? 8 : 10} className="main-column g-0">
                            <BreadCrumbTrail />
                            <div className="page-content m-3">
                                {
                                    pageTitleComponent ? pageTitleComponent : null
                                }
                                {contentComponent}
                            </div>
                        </Col>
                        {
                            sideBarComponent &&
                            <Col md={2}>
                                {sideBarComponent}
                            </Col>
                        }
                    </Row>
                </Container>
            </div>
        </Authorized >
    )
};
export interface PageTitleProps {
    text?: string | JSX.Element;
    subText?: string | JSX.Element;
    className?: string;
    inlineBlock?: boolean;
}

const PageTitle = (props: React.PropsWithChildren<PageTitleProps>) => {

    if (props.text) {
        return (
            <>
                <h1 className={classNames("page-header", { "d-inline-block": props.inlineBlock, 'd-flex': !props.inlineBlock }, props.className)}>{props.text}{props.children ? props.children : <></>}</h1>
                {props.subText &&
                    <div className="page-subheader">{props.subText}</div>
                }
            </>
        )
    }
    return (
        <>
            {props.children ? props.children : <></>}
        </>
    )
}
export interface SideBarProps {
    title?: string;
    className?: string;
}

const SideBar = (props: React.PropsWithChildren<SideBarProps>) => {
    return (
        <>
            <div className={classNames("page-sideBar page-sideBar__right", props.className)}>
                {props.title &&
                    <h3 className="title">{props.title}</h3>
                }
                {props.children}
            </div>
        </>
    )
}

export interface ContentProps {
}

const Content = (props: React.PropsWithChildren<ContentProps>) => {
    return (
        <>
            {props.children}
        </>
    );
}

interface IPageNavProps {

}

const PageNav = (props: React.PropsWithChildren<IPageNavProps>) => {
    return (
        <>
            {props.children}
        </>
    );
}


const PageComponent = Object.assign(Page, {
    SideBar,
    Content,
    PageTitle,
    PageNav
});

const AdminPageComponent = Object.assign(AdminPage, {
    SideBar,
    Content,
    PageTitle
});

export {
    AdminPageComponent as AdminPage, PageComponent as Page
};

interface IMainContentProps {
    cols?: number,
    showBreadcrumbs?: boolean,
    title?: string,
}

export const MainContent = ({ cols = 10, showBreadcrumbs = true, ...props }: React.PropsWithChildren<IMainContentProps>) => (
    <Col md={cols} className="main-column g-0">
        {showBreadcrumbs && <BreadCrumbTrail />}
        <div className="page-content m-3">
            {props.title && <PageTitle text={props.title}></PageTitle>}
            <Content>
                {props.children}
            </Content>
        </div>
    </Col>
);


interface ISidebarProps {
    side: "left" | "right"
    title?: string;
    className?: string;
    cols?: number
    minWidth?: string
}

export const Sidebar = ({ cols = 2, minWidth = "0px", ...props }: React.PropsWithChildren<ISidebarProps>) => (
    <Col md={cols} style={{ minWidth: minWidth }}>
        <div className={classNames("page-sideBar", `page-sideBar__${props.side}`, props.className)}>
            {props.title && <h3 className="title">{props.title}</h3>}
            {props.children}
        </div>
    </Col>
)
